import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext } from './AuthContext';

// "Admin mode" is a UI lens, not a permissions change. The same user is
// always behind the request — `is_admin` is what gates the actual /api/admin
// endpoints. This context just hides the regular platform chrome (notifications,
// non-admin nav links) so the admin can focus on moderation without distraction.
//
// Persisted in localStorage so a refresh keeps the lens. Cleared on logout
// (when user goes null we force adminMode off in the effect below).
export const AdminModeContext = createContext({
  adminMode: false,
  setAdminMode: () => {},
  toggleAdminMode: () => {},
});

const STORAGE_KEY = 'adminMode';

export const AdminModeProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  // Synchronous hydration mirrors AuthContext above. Reads the cached
  // user from localStorage to decide whether the lens was on, so the
  // banner is correct on first paint instead of popping in 1-2 frames
  // later. Without this, the dashboard rendered banner-less and then
  // re-rendered with the amber stripe — the "Welcome Admin" flash
  // users reported.
  const [adminMode, setAdminModeState] = useState(() => {
    try {
      const u = localStorage.getItem('user');
      if (!u) return false;
      const parsed = JSON.parse(u);
      return parsed?.isAdmin === true && localStorage.getItem(STORAGE_KEY) === '1';
    } catch { return false; }
  });

  // Reactive sync for events the lazy init can't see: login after
  // blank storage (user changes from null → admin), logout, and
  // admin-revoked. The lazy init covers the refresh-with-cached-
  // session case, which is the one that flashed.
  useEffect(() => {
    if (user?.isAdmin && localStorage.getItem(STORAGE_KEY) === '1' && !adminMode) {
      setAdminModeState(true);
    } else if (!user?.isAdmin && adminMode) {
      // User logged out, downgraded, or switched accounts — drop the lens.
      setAdminModeState(false);
    }
  }, [user, adminMode]);

  const setAdminMode = useCallback((v) => {
    setAdminModeState(v);
    if (v) localStorage.setItem(STORAGE_KEY, '1');
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const toggleAdminMode = useCallback(() => {
    setAdminMode(!adminMode);
  }, [adminMode, setAdminMode]);

  return (
    <AdminModeContext.Provider value={{ adminMode, setAdminMode, toggleAdminMode }}>
      {children}
    </AdminModeContext.Provider>
  );
};

export const useAdminMode = () => useContext(AdminModeContext);
